import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Tag, Trash2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import api from "../../lib/api";
import { errorMessage } from "../../lib/stays";

const iso = (d) => d.toISOString().split("T")[0];
const blank = () => ({ code: "", discount_type: "percent", discount_value: 10, max_uses: 100, valid_from: iso(new Date()), valid_until: iso(new Date(Date.now() + 90 * 86400000)) });
const sel = "mt-2 w-full bg-[#0f0f0f] border border-white/10 h-12 px-3 text-sm";

export default function Coupons() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/coupons")).data); } catch (e) { toast.error(errorMessage(e, "Couldn't load coupons")); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.code.trim()) { toast.error("Enter a coupon code"); return; }
    setBusy(true);
    try {
      await api.post("/admin/coupons", { ...form, code: form.code.trim().toUpperCase(), discount_value: Number(form.discount_value), max_uses: Number(form.max_uses) });
      toast.success("Coupon created"); setForm(blank()); load();
    } catch (e) {
      const d = e.response?.data;
      toast.error(d && typeof d === "object" && !d.detail ? Object.entries(d).map(([k, v]) => `${k}: ${[].concat(v).join(" ")}`).join(" · ") : errorMessage(e));
    }
    setBusy(false);
  };
  const toggle = async (c) => { try { await api.patch(`/admin/coupons/${c.id}/update`, { is_active: !c.is_active }); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const remove = async (c) => { if (!window.confirm(`Delete coupon ${c.code}?`)) return; try { await api.delete(`/admin/coupons/${c.id}`); load(); } catch (e) { toast.error(errorMessage(e)); } };

  return (
    <div className="space-y-6" data-testid="admin-coupons">
      <div className="glass p-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><Label className="text-xs text-white/60 uppercase">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LOVE20" className="mt-2 bg-[#0f0f0f] border-white/10 h-12" data-testid="new-coupon-code" /></div>
          <div><Label className="text-xs text-white/60 uppercase">Type</Label>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className={sel}><option value="percent">Percent (%)</option><option value="fixed">Fixed (₹)</option></select></div>
          <div><Label className="text-xs text-white/60 uppercase">Value ({form.discount_type === "percent" ? "%" : "₹"})</Label><Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" /></div>
          <div><Label className="text-xs text-white/60 uppercase">Valid from</Label><Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" /></div>
          <div><Label className="text-xs text-white/60 uppercase">Valid until</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" /></div>
          <div><Label className="text-xs text-white/60 uppercase">Max uses</Label><Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" /></div>
        </div>
        <button onClick={add} disabled={busy} className="btn-gold mt-5 disabled:opacity-40" data-testid="add-coupon-button">Add coupon</button>
      </div>

      {rows === null ? <div className="glass h-24 animate-pulse" /> : rows.length === 0 ? <div className="text-center text-white/40 py-8">No coupons yet</div> : (
        <div className="space-y-3">
          {rows.map((c) => (
            <div key={c.id} className={`glass p-4 flex items-center gap-4 ${c.is_active ? "" : "opacity-50"}`} data-testid={`coupon-${c.code}`}>
              <Tag className="text-gold shrink-0" size={16} />
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-white">{c.code}</div>
                <div className="text-xs text-white/50">
                  {c.discount_type === "percent" ? `${c.discount_value}% off` : `₹${c.discount_value} off`} · used {c.uses_count ?? 0}/{c.max_uses} · {c.valid_from} → {c.valid_until} · {c.is_active ? "Active" : "Paused"}
                </div>
              </div>
              <button onClick={() => toggle(c)} aria-label={c.is_active ? `Pause ${c.code}` : `Activate ${c.code}`} className="text-white/60 hover:text-gold">{c.is_active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
              <button onClick={() => remove(c)} aria-label={`Delete ${c.code}`} className="text-red-400" data-testid={`delete-coupon-${c.code}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
